import React, { FC, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';
import { find, findIndex } from 'lodash';
import { getTotalPriceForSlotBasedSubscription } from '@src/util/pricing';
import { Button } from '@src/components/Button';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { INACTIVE_SUBSCRIPTION_STATES, LOCKED_STATES } from '@src/constants/subscription';
import {
  SUBSCRIPTION_UNLIMITED_BENEFITS,
  SUBSCRIPTION_BASIC_BENEFITS,
  SUBSCRIPTION_STARTER_BENEFITS,
} from '@src/constants';
import {
  isSubscriptionStarter,
  isSubscriptionBasic,
  isSubscriptionUnlimited,
  isEnterprise,
  isCandidatePlan,
  isSlotBasedSubscription,
  isSubscriptionLimitedLowBase,
  isSubscriptionLimitedHighBase,
} from '@src/util/planTypes';
import { openActivateSubscriptionModal } from '@src/redux/actions/subscription';
import { useBillingSettingsSubscriptionPlansQuery } from '@src/graphql/generated';
import { SubscriptionPlansContainer } from './SubscriptionPlansContainer';
import {
  SubscribeButton,
  UpgradeButton,
  DowngradePlanButton,
  CancelPlanButton,
  SeePlansButton,
  EditPlanButton,
} from './SubscriptionPlansButtons';
import { NextPaymentDateStatus } from './NextPaymentDateStatus';
import LockedStatusMessage from './LockedStatusMessage';
import SubscribedTrialNotEnded from './SubscribedTrialNotEnded';
import Loading from './Loading';
import { SubscriptionPlansContainerProps } from './SubscriptionPlansContainer/props';

const SubscriptionPlans: FC = ({ setErrorFlashMessage, openActivateSubscriptionModal }) => {
  const { loading, error, data } = useBillingSettingsSubscriptionPlansQuery({
    fetchPolicy: 'network-only',
    onError: () => setErrorFlashMessage(),
  });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return null;
  }

  const {
    availableCurrencies,
    subscriptionPlans,
    customer: {
      planType: customerPlanType,
      id: customerId,
      planInfo: {
        state,
        cycle,
        price,
        occupiedOpeningSlots,
        openingSlots,
        slotPrice,
        nextPaymentDate,
        trialEndsAt,
        currency: { symbol },
        openingActivationPrice,
      },
    },
  } = data;

  if (INACTIVE_SUBSCRIPTION_STATES.includes(state)) {
    return (
      <SubscriptionPlansContainer
        benefits={SUBSCRIPTION_STARTER_BENEFITS}
        buttons={
          <Fragment>
            <Button onClick={() => openActivateSubscriptionModal()}>Select Plan</Button>
          </Fragment>
        }
        subtitle={'Subscribe and keep the access.'}
        title={'Subscription is not active'}
      />
    );
  } else if (LOCKED_STATES.includes(state)) {
    // if user is in locked state they should see subscription plan data provided from subscriptionPlans
    const { name, planType, variants } = find(subscriptionPlans, 'isAvailable');
    const { price, currency } = find(variants, { cycle });
    const { symbol } = find(availableCurrencies, { code: currency.toUpperCase() });

    return (
      <SubscriptionPlansContainer
        activeOpenings={{ amount: occupiedOpeningSlots, slots: openingSlots }}
        benefits={SUBSCRIPTION_STARTER_BENEFITS}
        buttons={
          <Fragment>
            <SubscribeButton
              currentPlanType={customerPlanType}
              customerId={customerId}
              nextPlanType={planType}
              subscriptionCycle={cycle}
              trialEndsAt={trialEndsAt}
            >
              Subscribe
            </SubscribeButton>

            <SeePlansButton />
          </Fragment>
        }
        footer={<LockedStatusMessage state={state} trialEndsAt={trialEndsAt} />}
        footerInfo="Subscribe and keep the access."
        subscriptionCycle={cycle}
        subscriptionPrice={symbol + price}
        title={name + ' plan'}
      />
    );
  } else if (isEnterprise(customerPlanType)) {
    return (
      <SubscriptionPlansContainer
        descriptionText="You are on Enterprise plan with Unlimited access."
        footerInfo={
          <Fragment>
            To get detailed information about your payments and subscription please contact your account manager or{' '}
            <a href={'mailto:support@meetfrank.com'}>support@meetfrank.com</a>.
          </Fragment>
        }
        title="Enterprise"
      />
    );
  } else if (isCandidatePlan(customerPlanType)) {
    return null;
  } else {
    const currentPlanIndex = findIndex(subscriptionPlans, { planType: customerPlanType });
    const { name } = subscriptionPlans[currentPlanIndex] || { name: 'Subscription' };

    const defaultProps: Partial<SubscriptionPlansContainerProps> = {
      currencySymbol: symbol,
      subscriptionCycle: cycle,
      subscriptionPrice: price,
      title: name + ' plan',
    };

    if (price > 0) {
      defaultProps.footer = moment().isBefore(moment(trialEndsAt)) ? (
        <SubscribedTrialNotEnded trialEndsAt={trialEndsAt} />
      ) : (
        <NextPaymentDateStatus nextPaymentDate={nextPaymentDate} />
      );
      defaultProps.footerInfo = 'Your subscription is being billed at the beginning of the renewal cycle.';
    }

    if (isSlotBasedSubscription(customerPlanType)) {
      const totalPrice = getTotalPriceForSlotBasedSubscription(price, slotPrice, openingSlots);

      return (
        <SubscriptionPlansContainer
          {...defaultProps}
          activeOpenings={{ amount: occupiedOpeningSlots, slots: openingSlots }}
          benefits={SUBSCRIPTION_STARTER_BENEFITS}
          buttons={
            <Fragment>
              <EditPlanButton />
              <CancelPlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />
            </Fragment>
          }
          slotPrice={slotPrice ? symbol + slotPrice : undefined}
          totalPrice={totalPrice || undefined}
        />
      );
    } else if (isSubscriptionStarter(customerPlanType)) {
      return (
        <SubscriptionPlansContainer
          {...defaultProps}
          activeOpenings={{ amount: occupiedOpeningSlots, slots: openingSlots }}
          benefits={SUBSCRIPTION_STARTER_BENEFITS}
          buttons={
            <Fragment>
              <UpgradeButton subscriptionCycle={cycle}>Upgrade</UpgradeButton>

              <CancelPlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />
            </Fragment>
          }
        />
      );
    } else if (isSubscriptionBasic(customerPlanType)) {
      return (
        <SubscriptionPlansContainer
          {...defaultProps}
          activeOpenings={{ amount: occupiedOpeningSlots, slots: openingSlots }}
          benefits={SUBSCRIPTION_BASIC_BENEFITS}
          buttons={
            <Fragment>
              <UpgradeButton subscriptionCycle={cycle}>Upgrade</UpgradeButton>

              <CancelPlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />
            </Fragment>
          }
        />
      );
    } else if (isSubscriptionUnlimited(customerPlanType)) {
      return (
        <SubscriptionPlansContainer
          {...defaultProps}
          benefits={SUBSCRIPTION_UNLIMITED_BENEFITS}
          buttons={
            <Fragment>
              <DowngradePlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />

              <CancelPlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />
            </Fragment>
          }
          descriptionText="Enjoy the unlimited plan."
        />
      );
    } else if (isSubscriptionLimitedLowBase(customerPlanType)) {
      return (
        <SubscriptionPlansContainer
          {...defaultProps}
          buttons={
            <Fragment>
              {/* <UpgradeButton subscriptionCycle={cycle}>Upgrade</UpgradeButton> */}

              <CancelPlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />
            </Fragment>
          }
          openingActivationPrice={openingActivationPrice}
        />
      );
    } else if (isSubscriptionLimitedHighBase(customerPlanType)) {
      return (
        <SubscriptionPlansContainer
          {...defaultProps}
          buttons={
            <Fragment>
              {/* <UpgradeButton subscriptionCycle={cycle}>Upgrade</UpgradeButton> */}

              <CancelPlanButton customerId={customerId} planType={customerPlanType} trialEndsAt={trialEndsAt} />
            </Fragment>
          }
          openingActivationPrice={openingActivationPrice}
        />
      );
    }

    return null;
  }
};

SubscriptionPlans.propTypes = {
  openActivateSubscriptionModal: PropTypes.func.isRequired,
  setErrorFlashMessage: PropTypes.func.isRequired,
};

export default connect(null, {
  openActivateSubscriptionModal,
  setErrorFlashMessage,
})(SubscriptionPlans);
